import React, { useState, useMemo, useEffect } from "react";
import { 
  TextField, 
  Button, 
  Stack, 
  Typography, 
  Paper, 
  Divider,
  Box,
  Container,
  Alert,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import { QrCode2 as QRCodeIcon, Event as EventIcon, Download as DownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { useUserContext } from "../context/UserContext";
import { createQREvent, listClassrooms, listClassroomBookings, createClassroomQREvent, isClassroomTimeAvailable, listTimetableEntriesByClassroom } from "../context/Api";
import QRCodeGenerator from "../components/QRCodeGenerator";
import PageNavBar from "../components/PageNavBar";
import TimePicker15Min from "../components/TimePicker15Min";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../firebase.config";

const AdminQREvent = () => {
  const navigate = useNavigate();
  const { userRole, tenantId, selectedTenantId, setSelectedTenantId, setSelectedTenantName } = useUserContext();
  const isAdmin = userRole === "admin" || userRole === "SuperAdmin";
  
  // For SuperAdmin, use selectedTenantId; for regular users, use tenantId
  const effectiveTenantId = userRole === "SuperAdmin" ? selectedTenantId : tenantId;
  
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState("100");
  const [createdEventId, setCreatedEventId] = useState(null);
  const [type, setType] = useState("event"); // "event" | "classroom"
  const [classrooms, setClassrooms] = useState([]);
  const [classroomId, setClassroomId] = useState("");
  const [bookings, setBookings] = useState([]);
  const [availabilityError, setAvailabilityError] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timetableEntries, setTimetableEntries] = useState([]);
  const [selectedLectureId, setSelectedLectureId] = useState("");
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedQRData, setSelectedQRData] = useState(null);

  // Generate QR payload with time encoding to prevent proxy
  const generateQRPayload = (type, id) => {
    const timestamp = Date.now();
    return JSON.stringify({ t: type, id, ts: timestamp });
  };

  const qrPayload = useMemo(() => {
    if (type === "classroom") {
      if (!classroomId) return "";
      // If a lecture is selected, use that for QR, otherwise use classroom
      if (selectedLectureId) {
        return generateQRPayload("lecture", selectedLectureId);
      }
      return generateQRPayload("classroom", classroomId);
    }
    if (!createdEventId) return "";
    return generateQRPayload("qr_event", createdEventId);
  }, [type, classroomId, createdEventId, selectedLectureId]);

  useEffect(() => {
    const fetchClassrooms = async () => {
      if (!effectiveTenantId) {
        console.log("No effectiveTenantId available for fetching classrooms");
        setError("No tenant ID available. Please select a tenant or refresh the page.");
        return;
      }
      
      setLoading(true);
      setError("");
      
      try {
        console.log("Fetching classrooms for effectiveTenantId:", effectiveTenantId);
        const list = await listClassrooms(effectiveTenantId);
        console.log("Fetched classrooms:", list);
        setClassrooms(list || []);
      } catch (err) {
        console.error("Error fetching classrooms:", err);
        setError("Failed to fetch classrooms: " + (err.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    };
    fetchClassrooms();
  }, [effectiveTenantId]);

  useEffect(() => {
    const fetchBookings = async () => {
      setAvailabilityError("");
      setBookings([]);
      if (type !== "classroom" || !effectiveTenantId || !classroomId || !date) return;
      const list = await listClassroomBookings(effectiveTenantId, classroomId, date);
      // sort by startTime
      list.sort((a, b) => (a.startTime > b.startTime ? 1 : -1));
      setBookings(list);
    };
    fetchBookings();
  }, [type, effectiveTenantId, classroomId, date]);

  // Fetch timetable entries when classroom is selected
  useEffect(() => {
    const fetchTimetableEntries = async () => {
      if (type !== "classroom" || !effectiveTenantId || !classroomId) {
        setTimetableEntries([]);
        return;
      }
      try {
        const entries = await listTimetableEntriesByClassroom(effectiveTenantId, classroomId);
        setTimetableEntries((entries || []).filter(e => e.active !== false));
      } catch (err) {
        console.error("Error fetching timetable entries:", err);
      }
    };
    fetchTimetableEntries();
  }, [type, effectiveTenantId, classroomId]);

  const handleCreate = async () => {
    if (!isAdmin) {
      setError("Only administrators can create QR events");
      return;
    }
    
    if (type === "event") {
      if (!name || !date || !startTime || !endTime) {
        setError("Please fill in all required fields");
        return;
      }
      
      if (!effectiveTenantId) {
        setError("No tenant ID available. Please select a tenant or refresh and try again.");
        return;
      }
      
      setLoading(true);
      setError("");
      setAvailabilityError("");
      
      try {
        if (!latitude || !longitude) {
          setError("Please provide latitude and longitude for event location");
          return;
        }
        const payload = {
          name,
          date,
          startTime,
          endTime,
          latitude: Number(latitude),
          longitude: Number(longitude),
          radius: Number(radius) || 100,
          tenantId: effectiveTenantId,
          createdBy: sessionStorage.getItem("userEmail") || "",
          type: "event",
        };
        const { id } = await createQREvent(payload);
        setCreatedEventId(id);
      } catch (e) {
        console.error("Error creating QR event:", e);
        setError(e?.message || "Failed to create QR event");
      } finally {
        setLoading(false);
      }
    }
    // For classroom type, we don't create anything - just show QR for selected classroom/event
  };

  // Auto-select tenant for admin users
  useEffect(() => {
    if (userRole === "admin" && tenantId && !selectedTenantId) {
      // Automatically set tenant for admin users
      setSelectedTenantId(tenantId);
      sessionStorage.setItem("selectedTenantId", tenantId);
    }
  }, [userRole, tenantId, selectedTenantId]);

  // Fetch tenants for SuperAdmin
  useEffect(() => {
    const fetchTenants = async () => {
      if (userRole === "SuperAdmin") {
        setTenantsLoading(true);
        try {
          const snapshot = await getDocs(collection(firestore, "TenantMaster"));
          const tenants = snapshot.docs.map((doc) => doc.data());
          setTenantList(tenants);
        } catch (err) {
          console.error("Error fetching tenants:", err);
          setError("Failed to fetch tenants: " + (err.message || "Unknown error"));
        } finally {
          setTenantsLoading(false);
        }
      }
    };
    fetchTenants();
  }, [userRole]);

  // Handle tenant selection for SuperAdmin
  const handleTenantChange = (e) => {
    const selectedId = e.target.value;
    const selected = tenantList.find((t) => t.tenantId === selectedId);
    
    setSelectedTenantId(selectedId);
    setSelectedTenantName(selected?.tenantName || "");
    
    // Save to sessionStorage so it persists
    sessionStorage.setItem("selectedTenantId", selectedId);
    sessionStorage.setItem("selectedTenantName", selected?.tenantName || "");
    
    // Clear any existing error
    setError("");
  };

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      py: 4,
      
    }}>
      <PageNavBar title="QR Event Management" backTo="/dashboard" />
      <Container maxWidth="lg" 
    sx={{ 
      mt: 3, // Solution 1
    }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "rgba(255, 255, 255, 0.98)" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <QRCodeIcon sx={{ fontSize: 40, color: "#1976d2", mr: 2 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                Create QR Event
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EventIcon />}
              onClick={() => navigate("/event-management")}
              sx={{ ml: 2 }}
            >
              Manage Events
            </Button>
          </Box>
          
          {/* Error Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Info for regular admins */}
          {userRole === "admin" && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Admin Access:</strong> You can create QR events and manage events for your tenant.
                {!effectiveTenantId && (
                  <span style={{ color: "#d32f2f", fontWeight: "bold" }}>
                    {" "}Warning: No tenant ID found. Please refresh the page or contact support.
                  </span>
                )}
              </Typography>
            </Alert>
          )}

          {/* Tenant Selection for SuperAdmin */}
          {userRole === "SuperAdmin" && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>SuperAdmin:</strong> Select a tenant to create QR events for that organization.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tenant</InputLabel>
                <Select
                  value={selectedTenantId || ""}
                  onChange={handleTenantChange}
                  label="Select Tenant"
                  disabled={tenantsLoading}
                >
                  {tenantsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading tenants...
                    </MenuItem>
                  ) : (
                    tenantList.map((tenant) => (
                      <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.tenantName || tenant.tenantId}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <ToggleButtonGroup
                  value={type}
                  exclusive
                  onChange={(e, v) => {
                    if (v) {
                      setType(v);
                      if (v === "classroom") {
                        // Clear form when switching to classroom
                        setName("");
                        setDate("");
                        setStartTime("");
                        setEndTime("");
                        setCreatedEventId(null);
                      }
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="event">Event</ToggleButton>
                  <ToggleButton value="classroom">Classroom</ToggleButton>
                </ToggleButtonGroup>
                {type === "event" && (
                  <>
                    <TextField 
                      label="Event Name" 
                      value={name} 
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      variant="outlined"
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField 
                          type="date" 
                          label="Date" 
                          InputLabelProps={{ shrink: true }} 
                          value={date} 
                          onChange={(e) => setDate(e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TimePicker15Min
                          label="Start Time"
                          value={startTime}
                          onChange={setStartTime}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TimePicker15Min
                          label="End Time"
                          value={endTime}
                          onChange={setEndTime}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                {type === "event" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        label="Latitude" 
                        value={latitude} 
                        onChange={(e) => setLatitude(e.target.value)}
                        fullWidth
                        placeholder="e.g., 28.6139"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        label="Longitude" 
                        value={longitude} 
                        onChange={(e) => setLongitude(e.target.value)}
                        fullWidth
                        placeholder="e.g., 77.2090"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        label="Radius (meters)" 
                        value={radius} 
                        onChange={(e) => setRadius(e.target.value)}
                        fullWidth
                        placeholder="100"
                      />
                    </Grid>
                  </Grid>
                )}
                {type === "classroom" && (
                  <>
                    <TextField
                      select
                      label="Select Classroom"
                      value={classroomId}
                      onChange={(e) => {
                        setClassroomId(e.target.value);
                        setSelectedLectureId("");
                      }}
                      fullWidth
                      disabled={loading || !effectiveTenantId}
                      helperText={
                        loading 
                          ? "Loading classrooms..." 
                          : !effectiveTenantId 
                            ? "Please select a tenant first" 
                            : classrooms.length === 0 
                              ? "No classrooms found for this tenant" 
                              : ""
                      }
                    >
                      {loading ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading classrooms...
                        </MenuItem>
                      ) : classrooms.length === 0 ? (
                        <MenuItem disabled>No classrooms available</MenuItem>
                      ) : (
                        classrooms.map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.name || c.id}</MenuItem>
                        ))
                      )}
                    </TextField>
                    {classroomId && timetableEntries.length > 0 && (
                      <TextField
                        select
                        label="Select Event (Optional)"
                        value={selectedLectureId}
                        onChange={(e) => setSelectedLectureId(e.target.value)}
                        fullWidth
                        helperText="Select an event to generate QR for that specific event"
                      >
                        <MenuItem value="">None (Use Classroom QR)</MenuItem>
                        {timetableEntries.map((entry) => {
                          const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                          return (
                            <MenuItem key={entry.id} value={entry.id}>
                              {entry.title} - {weekdayNames[entry.weekday]} ({entry.startTime} - {entry.endTime})
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    )}
                  </>
                )}
                {type === "event" && (
                  <>
                    {availabilityError && (
                      <Alert severity="error">{availabilityError}</Alert>
                    )}
                    <Button 
                      variant="contained" 
                      size="large"
                      disabled={!isAdmin || !name || !date || !startTime || !endTime || (!latitude || !longitude) || loading || !effectiveTenantId} 
                      onClick={handleCreate}
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                      sx={{ mt: 2 }}
                    >
                      {loading ? "Creating..." : "Create Event & Generate QR"}
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {qrPayload ? (
                <Box>
                  <QRCodeGenerator 
                    value={qrPayload} 
                    eventName={selectedLectureId 
                      ? timetableEntries.find(e => e.id === selectedLectureId)?.title || name
                      : name
                    } 
                    size={280} 
                  />
                  <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        setSelectedQRData({ value: qrPayload, name: selectedLectureId 
                          ? timetableEntries.find(e => e.id === selectedLectureId)?.title || name
                          : name });
                        setQrDialogOpen(true);
                      }}
                    >
                      View QR
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        const canvas = document.querySelector('canvas');
                        if (canvas) {
                          const url = canvas.toDataURL();
                          const link = document.createElement('a');
                          link.download = `QR-${name || 'code'}.png`;
                          link.href = url;
                          link.click();
                        }
                      }}
                    >
                      Download QR
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 400, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  border: '2px dashed #ccc'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    {type === "classroom" ? "Select a classroom to view its QR" : "Fill the form and click \"Create Event\" to generate QR code"}
                  </Typography>
                </Box>
              )}
              {type === "classroom" && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Bookings for {date || "selected date"}</Typography>
                  {(!classroomId || !date) ? (
                    <Typography variant="body2" color="text.secondary">Select a classroom and date to view timetable.</Typography>
                  ) : (
                    <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto' }}>
                      {bookings.length === 0 ? (
                        <Typography variant="body2" sx={{ p: 2 }}>No bookings. Entire day is available.</Typography>
                      ) : (
                        <List dense>
                          {bookings.map((b) => (
                            <ListItem key={b.id}>
                              <ListItemText primary={`${b.startTime} - ${b.endTime}`} secondary={b.name} />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Paper>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* QR View Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QR Code - {selectedQRData?.name || "QR Code"}
          <IconButton
            aria-label="close"
            onClick={() => setQrDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <EventIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {selectedQRData && (
            <QRCodeGenerator
              value={selectedQRData.value}
              eventName={selectedQRData.name}
              size={300}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => {
              const canvas = document.querySelector('canvas');
              if (canvas) {
                const url = canvas.toDataURL();
                const link = document.createElement('a');
                link.download = `QR-${selectedQRData?.name || 'code'}.png`;
                link.href = url;
                link.click();
              }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminQREvent;


